import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";

const Option = ({ d, choisi, onChoisir, plusieurs }) => (
  <label className={`flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors hover:bg-slate-50 ${choisi ? "bg-emerald-50/70" : ""}`} data-testid={`associer-depot-option-${d.id}`}>
    <input type="radio" name="depot" checked={choisi} onChange={() => onChoisir(d.id)} className="accent-emerald-700" data-testid={`associer-depot-radio-${d.id}`} />
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium" title={d.description}>{d.description}</p>
      <p className="text-xs text-slate-500">{d.date} · {d.jours >= 0 ? `${d.jours} j après` : `${-d.jours} j avant`} {plusieurs ? "la dernière réservation" : "la réservation"}</p>
      {d.deja > 0 && <p className="text-[11px] text-sky-700" data-testid={`associer-depot-deja-${d.id}`}>Déjà associé à {d.deja} réservation(s) ({money(d.deja_total)}) : {plusieurs ? "celles-ci s'y ajouteront" : "celle-ci s'y ajoutera"}</p>}
    </div>
    <div className="text-right">
      <p className="tabular text-sm font-semibold text-emerald-700">{money(d.credit)}</p>
      <p className={`tabular text-[11px] ${Math.abs(d.ecart) <= 2 ? "text-emerald-700" : "text-amber-700"}`}>Écart {money(d.ecart)}</p>
    </div>
  </label>
);

export const AssocierDepotDialog = ({ resaIds, onClose, onDone }) => {
  const [data, setData] = useState(null);
  const [choix, setChoix] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const cle = resaIds?.join(",");

  useEffect(() => {
    if (!cle) { return; }
    setData(null);
    setChoix(null);
    api.post("/associations/candidats", { reservation_ids: cle.split(",") }).then((r) => {
      setData(r.data);
      setChoix(r.data.depots[0]?.id || null);
    }).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [cle]); // eslint-disable-line react-hooks/exhaustive-deps

  const valider = async () => {
    setEnvoi(true);
    try {
      await api.post("/associations", { bank_tx_id: choix, reservation_ids: cle.split(",") });
      toast.success(data.reservations.length > 1 ? `${data.reservations.length} réservations associées au dépôt` : `Réservation ${data.reservations[0].code} associée au dépôt`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setEnvoi(false);
    }
  };

  const rs = data?.reservations || [];
  const plusieurs = rs.length > 1;
  return (
    <Dialog open={!!cle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="associer-depot-dialog">
        <DialogHeader>
          <DialogTitle>Associer {plusieurs ? `${rs.length} réservations au même dépôt` : "à un dépôt"}</DialogTitle>
          <DialogDescription asChild>
            <div data-testid="associer-depot-resa">
              {rs.map((r) => (
                <p key={r.id}>Réservation <span className="font-mono">{r.code}</span> · {r.plateforme} · {r.date}{r.logement ? ` · ${r.logement}` : ""} · <b className="tabular">{money(r.total_verse)}</b></p>
              ))}
              {plusieurs && <p className="mt-1 font-medium text-slate-700" data-testid="associer-depot-total">Total : <span className="tabular">{money(data.total)}</span></p>}
            </div>
          </DialogDescription>
        </DialogHeader>
        {!data ? <div className="grid place-items-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-md border" data-testid="associer-depot-liste">
            {!data.depots.length && <p className="p-4 text-sm text-slate-500" data-testid="associer-depot-aucun">Aucun dépôt {rs[0].plateforme} entre 7 jours avant et 60 jours après {plusieurs ? "ces réservations" : "la réservation"}.</p>}
            {data.depots.map((d) => <Option key={d.id} d={d} choisi={choix === d.id} onChoisir={setChoix} plusieurs={plusieurs} />)}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="associer-depot-annuler">Annuler</Button>
          <Button disabled={!choix || envoi} onClick={valider} className="bg-emerald-700 hover:bg-emerald-800" data-testid="associer-depot-valider">
            {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Associer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
